import React, { useState, useMemo } from 'react';
import { ActivityFeedItem, PolicyType } from '../../types';
import { LoadingSpinner } from '../common/LoadingSpinner';
import {
  Activity,
  FileText,
  ShieldCheck,
  GitCompare,
  Clock,
  Search,
  ExternalLink,
  ChevronRight,
  Sparkles,
  Layers,
  ArrowUpRight,
} from 'lucide-react';

interface ActivityFeedProps {
  feedItems: ActivityFeedItem[];
  loading: boolean;
  error: string | null;
  onSelectCompanyPolicy: (
    companyId: string,
    policyType: PolicyType,
    baseRevId?: string,
    targetRevId?: string
  ) => void;
  onNavigateToCatalog: () => void;
}

export const ActivityFeed: React.FC<ActivityFeedProps> = ({
  feedItems,
  loading,
  error,
  onSelectCompanyPolicy,
  onNavigateToCatalog,
}) => {
  const [filterType, setFilterType] = useState<'all' | 'terms_of_service' | 'privacy_policy'>('all');
  const [searchQuery, setSearchQuery] = useState<string>('');

  const formatRelativeTime = (isoString?: string | null) => {
    if (!isoString) return 'Recently';
    const date = new Date(isoString);
    const now = new Date();
    const diffSec = Math.floor((now.getTime() - date.getTime()) / 1000);
    const diffMin = Math.floor(diffSec / 60);
    const diffHours = Math.floor(diffMin / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMin < 1) return 'Just now';
    if (diffMin < 60) return `${diffMin}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 30) return `${diffDays}d ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const getInitials = (name: string) => name.substring(0, 2).toUpperCase();

  const filteredItems = useMemo(() => {
    let result = [...feedItems];

    if (filterType !== 'all') {
      result = result.filter((item) => item.policyType === filterType);
    }

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase().trim();
      result = result.filter(
        (item) =>
          item.companyName.toLowerCase().includes(q) ||
          item.companyDomain.toLowerCase().includes(q) ||
          item.title.toLowerCase().includes(q) ||
          (item.headline && item.headline.toLowerCase().includes(q)) ||
          item.keyTakeaways?.some((k) => k.toLowerCase().includes(q))
      );
    }

    return result;
  }, [feedItems, filterType, searchQuery]);

  if (loading && feedItems.length === 0) {
    return (
      <div className="py-12">
        <LoadingSpinner message="Scanning live policy revisions & building activity feed..." />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Hero Stream Header */}
      <div className="bg-gradient-to-br from-indigo-900/90 via-slate-900 to-slate-950 text-white rounded-2xl p-6 sm:p-8 border border-indigo-500/20 shadow-xl relative overflow-hidden">
        <div className="absolute right-0 top-0 w-96 h-96 bg-indigo-500/10 rounded-full blur-3xl pointer-events-none" />
        <div className="relative z-10 max-w-3xl space-y-3">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-indigo-500/20 border border-indigo-400/30 text-indigo-300 text-xs font-semibold">
            <Activity className="w-3.5 h-3.5 animate-pulse text-indigo-400" />
            Live Policy Stream
          </div>
          <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight">
            Recent Terms & Privacy Updates
          </h1>
          <p className="text-slate-300 text-sm sm:text-base leading-relaxed">
            Real-time feed of detected modifications, AI-summarized takeaways, and diff timelines across monitored tech companies.
          </p>
        </div>
      </div>

      {/* Feed Controls: Search & Policy Filters */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-white dark:bg-slate-800 p-4 rounded-xl border border-slate-200 dark:border-slate-700/80 shadow-2xs">
        {/* Filter Pills */}
        <div className="flex items-center gap-1.5 overflow-x-auto pb-1 sm:pb-0">
          <button
            onClick={() => setFilterType('all')}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors flex items-center gap-1.5 ${
              filterType === 'all'
                ? 'bg-indigo-600 text-white shadow-xs'
                : 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600'
            }`}
          >
            <Activity className="w-3.5 h-3.5" />
            All Updates
            <span className="bg-white/20 dark:bg-slate-800/60 px-1.5 py-0.2 rounded-full text-[10px]">
              {feedItems.length}
            </span>
          </button>

          <button
            onClick={() => setFilterType('terms_of_service')}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors flex items-center gap-1.5 ${
              filterType === 'terms_of_service'
                ? 'bg-indigo-600 text-white shadow-xs'
                : 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600'
            }`}
          >
            <FileText className="w-3.5 h-3.5" />
            Terms of Service
          </button>

          <button
            onClick={() => setFilterType('privacy_policy')}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors flex items-center gap-1.5 ${
              filterType === 'privacy_policy'
                ? 'bg-teal-600 text-white shadow-xs'
                : 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600'
            }`}
          >
            <ShieldCheck className="w-3.5 h-3.5" />
            Privacy Policies
          </button>
        </div>

        {/* Feed Search Box */}
        <div className="relative flex-1 sm:max-w-xs">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search feed updates..."
            className="w-full pl-9 pr-3 py-1.5 text-xs rounded-lg bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
        </div>
      </div>

      {/* Feed List Items */}
      {filteredItems.length === 0 ? (
        <div className="bg-white dark:bg-slate-800 rounded-xl p-12 text-center border border-slate-200 dark:border-slate-700 space-y-3">
          <Activity className="w-10 h-10 text-slate-400 mx-auto" />
          <h3 className="text-base font-semibold text-slate-800 dark:text-slate-200">
            No updates match your filter
          </h3>
          <p className="text-xs text-slate-500 max-w-md mx-auto">
            {searchQuery ? 'Try clearing your search term.' : 'Browse the full catalog to explore tracked tech companies.'}
          </p>
          <button
            onClick={onNavigateToCatalog}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-semibold bg-indigo-600 text-white hover:bg-indigo-700 transition-colors"
          >
            <Layers className="w-3.5 h-3.5" />
            Browse All Companies
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredItems.map((item) => (
            <div
              key={item.id}
              className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700/80 shadow-2xs hover:shadow-md hover:border-slate-300 dark:hover:border-slate-600 transition-all p-5 flex flex-col sm:flex-row sm:items-start justify-between gap-5 group"
            >
              {/* Left Column: Company & Change Summary */}
              <div className="flex-1 space-y-3">
                {/* Header: Company + Policy Pill + Relative Time */}
                <div className="flex flex-wrap items-center gap-2.5">
                  {/* Clickable Logo */}
                  <button
                    onClick={() => onSelectCompanyPolicy(item.companyId, item.policyType)}
                    className="cursor-pointer focus:outline-none rounded-lg transition-transform hover:scale-105"
                    title={`View ${item.companyName} timeline`}
                  >
                    {item.companyLogo ? (
                      <img
                        src={item.companyLogo}
                        alt={item.companyName}
                        className="w-8 h-8 rounded-lg object-contain bg-slate-100 dark:bg-slate-700 p-1 border border-slate-200 dark:border-slate-600 flex-shrink-0"
                        onError={(e) => {
                          e.currentTarget.style.display = 'none';
                        }}
                      />
                    ) : (
                      <div className="w-8 h-8 rounded-lg bg-indigo-600 text-white font-bold text-xs flex items-center justify-center flex-shrink-0">
                        {getInitials(item.companyName)}
                      </div>
                    )}
                  </button>

                  <div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => onSelectCompanyPolicy(item.companyId, item.policyType)}
                        className="font-bold text-slate-900 dark:text-white text-sm hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors cursor-pointer text-left"
                        title={`View ${item.companyName} timeline`}
                      >
                        {item.companyName}
                      </button>
                      <span className="text-xs text-slate-400">•</span>
                      <span
                        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-semibold ${
                          item.policyType === 'terms_of_service'
                            ? 'bg-indigo-50 dark:bg-indigo-950/60 text-indigo-700 dark:text-indigo-300 border border-indigo-200 dark:border-indigo-800'
                            : 'bg-teal-50 dark:bg-teal-950/60 text-teal-700 dark:text-teal-300 border border-teal-200 dark:border-teal-800'
                        }`}
                      >
                        {item.policyType === 'terms_of_service' ? (
                          <FileText className="w-3 h-3" />
                        ) : (
                          <ShieldCheck className="w-3 h-3" />
                        )}
                        {item.policyName}
                      </span>
                    </div>
                  </div>

                  <span className="text-xs text-slate-500 dark:text-slate-400 ml-auto flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    {formatRelativeTime(item.timestamp)}
                  </span>
                </div>

                {/* Change Headline / Content */}
                <div className="space-y-1.5">
                  <p className="text-sm font-semibold text-slate-800 dark:text-slate-200">
                    {item.headline}
                  </p>

                  {item.keyTakeaways && item.keyTakeaways.length > 0 && (
                    <ul className="space-y-1 text-xs text-slate-600 dark:text-slate-300 pl-4 list-disc pt-1">
                      {item.keyTakeaways.slice(0, 3).map((bullet, idx) => (
                        <li key={idx} className="leading-relaxed">
                          {bullet}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                {/* Document Status & Word Count Meta */}
                <div className="flex items-center gap-3 text-xs text-slate-500 dark:text-slate-400 pt-1">
                  {item.isBaseline ? (
                    <span className="text-slate-400 italic">Baseline Snapshot</span>
                  ) : null}

                  {item.wordCount ? (
                    <span>{item.isBaseline ? '• ' : ''}{item.wordCount.toLocaleString()} words</span>
                  ) : null}
                </div>
              </div>

              {/* Right Column: CTA Buttons */}
              <div className="flex sm:flex-col items-center sm:items-end gap-2 flex-shrink-0 pt-2 sm:pt-0 border-t sm:border-t-0 border-slate-100 dark:border-slate-700/60">
                {!item.isBaseline && item.previousRevisionId ? (
                  <button
                    onClick={() =>
                      onSelectCompanyPolicy(
                        item.companyId,
                        item.policyType,
                        item.previousRevisionId!,
                        item.revisionId
                      )
                    }
                    className="w-full sm:w-auto inline-flex items-center justify-center gap-1.5 px-3.5 py-1.5 rounded-lg text-xs font-semibold bg-indigo-600 text-white hover:bg-indigo-700 transition-colors shadow-2xs"
                  >
                    <GitCompare className="w-3.5 h-3.5" />
                    View Visual Diff
                  </button>
                ) : null}

                <button
                  onClick={() => onSelectCompanyPolicy(item.companyId, item.policyType)}
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors"
                >
                  Full Timeline
                  <ChevronRight className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ActivityFeed;
